import { useAuth } from '@pma/stores/auth-store';
import {
  PROJECTS_BY_CURRENT_USER_FILTER,
  SERVER_URL,
} from '@pma/utils/api-routes';
import { ACCESS_TOKEN } from '@pma/utils/constants';
import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import axios from 'axios';
import { parseCookies } from 'nookies';
import {
  createContext,
  Dispatch,
  ReactElement,
  SetStateAction,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

interface ProjectContextParams {
  projectResult:
  | UseInfiniteQueryResult<InfiniteData<any, unknown>, Error>
  | undefined;
  projectPagination: {
    pageIndex: number;
    pageSize: number;
  };
  setProjectPagination: Dispatch<
    SetStateAction<{
      pageIndex: number;
      pageSize: number;
    }>
  >;
  pinnedProjectResult: UseQueryResult<any, Error> | undefined;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const defaultContext: ProjectContextParams = {
  projectResult: undefined,
  projectPagination: {
    pageSize: 25,
    pageIndex: 1,
  },
  setProjectPagination: () => { },
  pinnedProjectResult: undefined,
  isEditing: false,
  setIsEditing: () => {},
  searchTerm: '',
  setSearchTerm: () => {},
};

export const ProjectContext =
  createContext<ProjectContextParams>(defaultContext);

export const ProjectProvider = ({
  defaultPageSize,
  children,
}: {
  defaultPageSize?: number;
  children: ReactElement;
}) => {
  const accessToken = parseCookies()[ACCESS_TOKEN];
  const { authConfig } = useAuth();
  const [searchParam] = useSearchParams();
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: defaultPageSize ?? 25,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const orgId = authConfig?.last_organization_id;
  const statusId = searchParam.get('status');
  const ownerId = searchParam.get('ownerId');
  const showDeleted = searchParam.get('showDeleted');

  const getProjectsByCurrentUser = async (pageParam: number, orgId: string) => {
    const response = await axios.post(
      SERVER_URL + PROJECTS_BY_CURRENT_USER_FILTER,
      {
        paging_start_page: pageParam,
        paging_per_page: pagination.pageSize,
        status_id: statusId,
        owner_id: ownerId ? [ownerId]: [],
        by_organization_id: orgId,
        show_deleted: showDeleted === 'true',
        by_title: searchTerm || undefined,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const projectResult = useInfiniteQuery({
    queryKey: ["projects-context", statusId, ownerId, showDeleted, searchTerm],
    queryFn: ({ pageParam = 1 }) => getProjectsByCurrentUser(pageParam, orgId),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.nextPage) {
        return null
      }
      return lastPage?.nextPage
      // return 0
    },
  })

  const getPinnedProjects = async (orgId: string) => {
    const response = await axios.post(
      SERVER_URL + PROJECTS_BY_CURRENT_USER_FILTER,
      {
        by_organization_id: orgId,
        is_pinned: true,
      },
      {
        headers: {
          Authorization: 'Bearer ' + accessToken,
          'Content-Type': 'application/json',
        },
      },
    );
    return response.data;
  };

  const pinnedProjectResult = useQuery({
    queryKey: ['get-pinned-projects', orgId],
    enabled: false,
    queryFn: () => getPinnedProjects(orgId),
  });

  return (
    <ProjectContext.Provider
      value={{
        projectResult,
        projectPagination: pagination,
        setProjectPagination: setPagination,
        pinnedProjectResult,
        isEditing,
        setIsEditing,
        searchTerm,
        setSearchTerm,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};
